/**
 * Building the copy-paste curl for an operation, and running the same request
 * from the browser.
 *
 * The command and the request "Try it" fires are built from the same values by
 * the same function, so what the developer copies is what the page ran. That is
 * the whole point: a curl block that is written separately from the thing it
 * documents is a curl block that will one day be wrong.
 */
import type { Endpoint, Parameter } from './spec';
import { pathParameters, queryParameters, requestExample } from './spec';

/** What the developer has filled in for one operation. */
export interface RequestValues {
  /** Path placeholders, by name. */
  path: Record<string, string>;
  /** Query parameters, by name. Empty values are dropped. */
  query: Record<string, string>;
  /** The JSON body, as typed. */
  body: string;
}

export interface BuiltRequest {
  method: string;
  /** The absolute URL, with placeholders substituted and the query appended. */
  url: string;
  headers: Record<string, string>;
  body?: string;
}

/** The placeholder a curl example shows in place of a key nobody has pasted. */
export const KEY_PLACEHOLDER = 'rk_live_your_key_here';

/** Everything an operation needs filled in before it can be called. */
export function initialValues(endpoint: Endpoint): RequestValues {
  const path: Record<string, string> = {};
  for (const p of pathParameters(endpoint.operation)) path[p.name] = '';

  const query: Record<string, string> = {};
  for (const p of queryParameters(endpoint.operation)) {
    const preset = p.schema?.default;
    query[p.name] = preset === undefined || preset === null ? '' : String(preset);
  }

  const example = requestExample(endpoint.operation);
  return { path, query, body: example === undefined ? '' : JSON.stringify(example, null, 2) };
}

/**
 * Resolves an operation into a concrete request.
 *
 * A path placeholder nobody filled in keeps its `{name}` form rather than
 * becoming an empty segment: `/v1/contacts/{id}` is a readable example, and
 * `/v1/contacts/` is a different endpoint that would quietly succeed.
 */
export function buildRequest(
  endpoint: Endpoint,
  baseUrl: string,
  values: RequestValues,
  apiKey: string | null
): BuiltRequest {
  let path = endpoint.path;
  for (const [name, value] of Object.entries(values.path)) {
    const filled = value.trim();
    if (filled) path = path.replace(`{${name}}`, encodeURIComponent(filled));
  }

  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(values.query)) {
    const filled = value.trim();
    if (filled) search.set(name, filled);
  }
  const query = search.toString();

  const headers: Record<string, string> = { 'X-Api-Key': apiKey?.trim() || KEY_PLACEHOLDER };
  const body = endpoint.method === 'GET' ? undefined : values.body.trim() || undefined;
  if (body) headers['Content-Type'] = 'application/json';

  return {
    method: endpoint.method,
    url: `${baseUrl.replace(/\/+$/, '')}${path}${query ? `?${query}` : ''}`,
    headers,
    body
  };
}

/** The same request, written as a command a terminal will run. */
export function toCurl(request: BuiltRequest): string {
  const lines: string[] = [];
  const verb = request.method === 'GET' ? '' : ` -X ${request.method}`;
  lines.push(`curl${verb} "${request.url}"`);
  for (const [name, value] of Object.entries(request.headers)) {
    lines.push(`  -H "${name}: ${value}"`); // i18n-ignore: an HTTP header line, not copy
  }
  if (request.body) {
    // Single quotes around the body, so the JSON's own double quotes survive.
    // A body containing a single quote is closed and reopened the POSIX way,
    // because there is no escape for one inside a single-quoted string.
    lines.push(`  -d '${request.body.replace(/'/g, `'\\''`)}'`);
  }
  return lines.join(' \\\n');
}

/** True when the value the developer typed is not JSON we could send. */
export function bodyError(body: string): boolean {
  const trimmed = body.trim();
  if (!trimmed) return false;
  try {
    JSON.parse(trimmed);
    return false;
  } catch {
    return true;
  }
}

/** Names every path placeholder still unfilled, so the page can say which. */
export function missingPathValues(endpoint: Endpoint, values: RequestValues): string[] {
  return pathParameters(endpoint.operation)
    .filter((p: Parameter) => !values.path[p.name]?.trim())
    .map((p) => p.name);
}
